// Loads the entire brand_identity table once per session, cached for
// 24h via useCachedQuery. Powers the Tier-1.5 subtype soft-fallback
// in brandsForCanonical at runtime (vs the bundled BRAND_EXPERTISE
// JS file which only carries a few hand-curated entries).
//
// Egress posture per CLAUDE.md: brand_identity is a slow-changing
// public-read table (~few hundred rows max), so a 24h TTL via
// localStorage is the right default. New brands discovered by
// enrich-region-brands will appear after the next 24h refresh — or
// immediately on a forced refresh() call.
//
// Returned shape:
//   {
//     identities,    // Array of brand_identity rows
//     bySlug,        // Map<slug, row> for O(1) lookup
//     loading,
//     refresh,
//   }
//
// Callers pass `bySlug` (or `identities`) into brandsForCanonical via
// opts.brandIdentities.

import { useEffect, useId, useMemo, useRef } from "react";
import { supabase } from "./supabase";
import { useCachedQuery } from "./useCachedQuery";

const TTL_24H = 24 * 60 * 60 * 1000;

async function fetchAllBrandIdentities() {
  const { data, error } = await supabase
    .from("brand_identity")
    .select(
      "slug, display_name, parent_company, scope, tier, is_private_label, private_label_for, regions, primary_states, subtypes, hubs, variants, confidence, source",
    );
  if (error) {
    console.warn("[useBrandIdentity] fetch failed:", error.message);
    return [];
  }
  return data || [];
}

export function useBrandIdentity({ enabled = true } = {}) {
  const { data, loading, refresh } = useCachedQuery({
    cacheKey: "brand_identity:all",
    ttlMs: TTL_24H,
    fetcher: fetchAllBrandIdentities,
    enabled,
    defaultValue: [],
  });

  // Cache invalidation: subscribe to regional_brand_enrichment and
  // refresh whenever ANY region's status flips to "done". Brand
  // identity rows are populated by the auto-trigger (or by the
  // bulk script) shortly after a region drains, so the user
  // shouldn't have to wait the full 24h cache TTL to see the new
  // brands their just-added city unlocked. A 24h TTL is correct as
  // a baseline (brands are slow-changing); this pushes a refresh
  // when we have direct evidence new ones likely landed.
  const refreshRef = useRef(refresh);
  useEffect(() => { refreshRef.current = refresh; }, [refresh]);

  // Channel name MUST be unique per mount — two components calling
  // supabase.channel() with the same name crashes the whole tree with
  // "cannot add postgres_changes once subscribed". useId() suffixes
  // the name so concurrent mounts coexist. (Egress note: this is a
  // realtime sub on a global table, normally banned — kept because
  // regional_brand_enrichment status flips are rare events, not a
  // hot-row broadcast; it exists purely to invalidate the 24h cache.)
  const channelId = useId();
  useEffect(() => {
    if (!enabled) return;
    const channel = supabase
      .channel(`region-enrichment-completion-watcher:${channelId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "regional_brand_enrichment",
        },
        (payload) => {
          const newStatus = payload?.new?.status;
          const oldStatus = payload?.old?.status;
          if (newStatus === "done" && oldStatus !== "done") {
            console.info(
              "[useBrandIdentity] region enrichment completed; refreshing brand_identity cache:",
              payload?.new?.region_key,
            );
            refreshRef.current?.();
          }
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [enabled, channelId]);

  const identities = Array.isArray(data) ? data : [];
  const bySlug = useMemo(() => {
    const m = new Map();
    for (const row of identities) {
      if (row?.slug) m.set(row.slug, row);
    }
    return m;
  }, [identities]);

  return { identities, bySlug, loading, refresh };
}
